import { AuthStatus, DivisionReportsApi, ReportsApi, UsageVarianceOutputType } from "@asd/customer-portal-api";
import { AxiosResponse, RawAxiosRequestConfig } from "axios";

import AxiosConfigurations, { AxiosConfiguration } from "./AxiosConfiguration";

import { axiosInstance } from ".";

export class ReportsService {
  static instance: ReportsService;
  private reportsApi;
  private divisionReportApi;
  private axiosConfigurationReports: AxiosConfiguration | undefined;
  private axiosConfigurationDivisionReports: AxiosConfiguration | undefined;

  constructor() {
    if (ReportsService.instance) {
      return ReportsService.instance;
    }

    this.reportsApi = new ReportsApi(undefined, undefined, axiosInstance);
    this.divisionReportApi = new DivisionReportsApi(undefined, undefined, axiosInstance);
    const reportsConfig = AxiosConfigurations.createAxiosConfiguration("reportsService", this.reportsApi);
    const divisionReportsConfig = AxiosConfigurations.createAxiosConfiguration("divisionReportsService", this.divisionReportApi);
    if (!reportsConfig) {
      throw new Error("Failed to create AxiosConfiguration for reportsService");
    }
    if (!divisionReportsConfig) {
      throw new Error("Failed to create AxiosConfiguration for divisionReportsService");
    }
    this.axiosConfigurationReports = reportsConfig;
    this.axiosConfigurationDivisionReports = divisionReportsConfig;

    ReportsService.instance = this;
  }

  public getPendingRequestCount(type: "reports" | "divisionReports") {
    return (type === "reports" ? this.axiosConfigurationReports : this.axiosConfigurationDivisionReports)?.getPendingRequestCount();
  }

  public async getSummary(
    reportDate: string,
    participantId?: string,
    reportCount?: number,
    riskScoreThreshold?: number,
    overusageVariancePercent?: number,
    underusageVariancePercent?: number,
    underusageReportingThreshold?: string,
    overagePercent?: number,
    status?: AuthStatus,
    options?: RawAxiosRequestConfig,
  ) {
    if (!this.reportsApi) {
      throw new Error("reportsApi is not initialized");
    }

    try {
      const response: AxiosResponse = await this.reportsApi.getHighlightsSummaries(
        reportDate,
        participantId,
        reportCount,
        riskScoreThreshold,
        overusageVariancePercent,
        underusageVariancePercent,
        underusageReportingThreshold,
        overagePercent,
        status,
        options,
      );
      return response;
    } catch {
      // do nothing
    }
  }

  public async getUsageVariance(
    varianceDate: string,
    participantId?: string,
    variancePercent?: number,
    reportingThreshold?: string,
    outputType?: UsageVarianceOutputType,
    status?: AuthStatus,
    options?: RawAxiosRequestConfig,
  ) {
    if (!this.reportsApi) {
      throw new Error("reportsApi is not initialized");
    }

    try {
      const response: AxiosResponse = await this.reportsApi.getUsageVariance(
        varianceDate,
        participantId,
        variancePercent,
        reportingThreshold,
        outputType,
        status,
        options,
      );
      return response;
    } catch {
      // do nothing
    }
  }

  public async getUsageDepleted(
    depletionDate: string,
    participantId?: string,
    overagePercent?: number,
    status?: AuthStatus,
    options?: RawAxiosRequestConfig,
  ) {
    if (!this.reportsApi) {
      throw new Error("reportsApi is not initialized");
    }

    try {
      const response: AxiosResponse = await this.reportsApi.getUsageDepleted(depletionDate, participantId, overagePercent, status, options);
      return response;
    } catch {
      // do nothing
    }
  }

  public async getOvertimeUsage(reportStart: string, reportStop: string, participantId?: string, options?: RawAxiosRequestConfig) {
    if (!this.reportsApi) {
      throw new Error("reportsApi is not initialized");
    }

    try {
      const response: AxiosResponse = await this.reportsApi.getOvertimeUsage(reportStart, reportStop, participantId, options);
      return response;
    } catch {
      // do nothing
    }
  }

  public async getDivisionReport(reportDate: string, options?: RawAxiosRequestConfig) {
    if (!this.divisionReportApi) {
      throw new Error("divisionReportApi is not initialized");
    }

    try {
      const response: AxiosResponse = await this.divisionReportApi.getDivisionReport(reportDate, options);
      return response;
    } catch {
      // do nothing
    }
  }
}

export default new ReportsService();
